import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { showDialog } from "@widgets/CommonDialog";
import { callApi, fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";
import SnTab from "@shared/components/SnTab";
import CommonSpinner from "@widgets/CommonSpinner";
import { SnGridRef, ColumnConfig } from "@shared/components/GridV2/SnGridTypes";
import { createPivotGridData } from "@menu/PMS/SN_PMS_PJT_REG_022/pivotUtils";
import {
  computeSalaryDistribution,
  AllocRow,
} from "./salaryAllocation";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** 노무비배부 피벗 컬럼(동적) 생성 헬퍼 — 모두 읽기전용. */
const mkCol = (
  cd_column: string,
  nm_column: string,
  tp_column: string,
  no_seq: number,
  width: number
): ColumnConfig => ({
  cd_firm: "",
  cd_menu: "SN_PMS_CLS_REG_002_POP_01",
  cd_grid: "grid3",
  cd_user: "",
  cd_column,
  nm_column,
  tp_column,
  fg_visible: "Y",
  no_seq,
  qt_width: width,
  yn_required: "N",
  yn_allowsorting: "N",
  yn_fixed: "N",
  yn_sum: "N",
  dc_editcon: "true", // 읽기전용
});

/** projects → 담당자 · 급여 · 프로젝트들 · 합계 컬럼 */
const buildDistColumns = (
  projects: { field: string; nm_pjt: string }[]
): ColumnConfig[] => {
  let seq = 1;
  return [
    mkCol("nm_emp", "담당자", "SnTextBox", seq++, 120),
    mkCol("am_pay", "급여", "SnNumberBox", seq++, 120),
    ...projects.map((p) => mkCol(p.field, p.nm_pjt, "SnNumberBox", seq++, 110)),
    mkCol("am_total", "합계", "SnNumberBox", seq++, 120),
  ];
};

// TEST: douzone 실 급여가 없을 때 배부 검증용 테스트 데이터. 실 연동 확정되면 제거.
const TEST_PAY = [2000000, 2500000, 3000000, 2200000, 2800000];
const TEST_ETC = 5000000;
const buildSyntheticPivot = (): {
  cols: { cd_column: string; nm_column: string }[];
  rows: any[];
} => ({
  cols: [
    { cd_column: "cd_emp", nm_column: "담당자" },
    { cd_column: "nm_emp", nm_column: "담당자" },
    { cd_column: "pjt_SPCC", nm_column: "SPCC" },
    { cd_column: "pjt_DUMP", nm_column: "DUMP" },
    { cd_column: "pjt_SSX", nm_column: "SSX1.SBSS" },
    { cd_column: "pjt_SD15", nm_column: "SD15" },
  ],
  rows: [
    { cd_emp: "T1", nm_emp: "박준홍(T)", pjt_SPCC: 0, pjt_DUMP: 50, pjt_SSX: 0, pjt_SD15: 50 },
    { cd_emp: "T2", nm_emp: "홍길동(T)", pjt_SPCC: 30, pjt_DUMP: 30, pjt_SSX: 30, pjt_SD15: 10 },
    { cd_emp: "T3", nm_emp: "김철수(T)", pjt_SPCC: 25, pjt_DUMP: 25, pjt_SSX: 25, pjt_SD15: 25 },
    { cd_emp: "T4", nm_emp: "유관순(T)", pjt_SPCC: 100, pjt_DUMP: 0, pjt_SSX: 0, pjt_SD15: 0 },
  ],
});

const SN_PMS_CLS_REG_002_POP_01 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();

    const searchRef = useRef<any>(null);
    const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
      grid1: React.createRef<SnGridRef>(),
      grid2: React.createRef<SnGridRef>(),
      grid3: React.createRef<SnGridRef>(),
    });

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [gridMeta, setGridMeta] = useState<{ [key: string]: ColumnConfig[] }>({});
    const [tab, setTab] = useState<string>("1"); // SnTab은 1-based 문자열: "1" Pivot · "2" 기타노무비입력 · "3" 노무비배부
    const [etcRows, setEtcRows] = useState<any[]>([]);
    const [pivotColumns, setPivotColumns] = useState<ColumnConfig[]>([]);
    const [pivotRows, setPivotRows] = useState<any[]>([]);
    const [distColumns, setDistColumns] = useState<ColumnConfig[]>([]);
    const [distRows, setDistRows] = useState<any[]>([]);
    const [projectTotals, setProjectTotals] = useState<AllocRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    // Pivot 비율 Pivot 의 기본 컬럼(cd_emp/nm_emp/total_value) — 재조회 시 재사용.
    const pivotBaseRef = useRef<ColumnConfig[]>([]);

    // 평가기간(YYYYMM) / 사업장 — 초기값은 메인 selectedData, 이후 팝업 SearchPanel 변경에 반응.
    const [cd_biz, setCdBiz] = useState<string>(selectedData?.cd_biz ?? "");
    const [dt_from, setDtFrom] = useState<string>(selectedData?.dt_from ?? "");
    const [dt_to, setDtTo] = useState<string>(selectedData?.dt_to ?? "");
    const ym_end = String(dt_to).slice(0, 6);

    // SearchPanel 값 변경 → 사업장/평가기간을 동기화. rowData는 전체 폼(baseFormData)이라 그대로 읽는다.
    const onValueChanged = (e: any) => {
      const d = e?.rowData ?? {};
      if ("cd_biz" in d) setCdBiz(d.cd_biz ?? "");
      if ("dt_from" in d) setDtFrom(d.dt_from ?? "");
      if ("dt_to" in d) setDtTo(d.dt_to ?? "");
    };

    // 집계 시 프로젝트별 노무비(급여/기타/합계)를 메인으로 반환한다.
    useImperativeHandle(ref, () => ({
      getSelectedValue: () =>
        projectTotals.length
          ? { code: "POPUP_POST", data: { cd_biz, ym_end, alloc: projectTotals } }
          : undefined,
    }));

    const getGridCurrent = (key: string): SnGridRef => {
      const grid = gridRefs.current[key]?.current;
      if (!grid) {
        throw new Error(`Grid '${key}' is not mounted yet`);
      }
      return grid;
    };

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData("SN_PMS_CLS_REG_002_POP_01", {});
          setFormControlData(rtn.formControlData);
          setFormData({ ...rtn.formData, ...selectedData });
          setGridMeta(rtn.grid ?? {});
          pivotBaseRef.current = rtn.grid?.grid1 ?? [];
        } catch (error) {}
      };
      fetchform();
    }, [userInfo]);

    // Pivot 탭 — 공수실적등록(SN_PMS_PJT_REG_022) 비율 Pivot 재사용.
    const loadPivot = async () => {
      if (!cd_biz) return { columns: [] as ColumnConfig[], rows: [] as any[] };
      const data: any = await fnGetDataSet({
        id: "sn_pms_pjt_reg_022_01",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_biz,
          dt_from,
          dt_to,
          cd_emp: "",
          cd_pjt: "",
          cd_dept: "",
          tp_view: "RATE", // 비율로 조회 — 급여 배부 가중치
        },
      });
      const pivot = createPivotGridData(data?.[2] || [], pivotBaseRef.current);
      setPivotColumns(pivot.columns);
      setPivotRows(pivot.rows);
      return pivot;
    };

    // 기타노무비입력 탭 — COS009 항목 전체 표시 + 저장된 금액. 항목명 읽기전용, 금액만 입력.
    const loadEtcLabor = async () => {
      if (!cd_biz || !ym_end) return;
      try {
        const rtn: any = await fnGetDataSet({
          id: "sn_pms_cls_reg_002_pop_01_01",
          params: { cd_firm: userInfo.cd_firm, cd_biz, ym_end },
        });
        setEtcRows(Array.isArray(rtn?.[0]) ? rtn[0] : []);
      } catch (error: any) {
        showDialog("error", error.message);
      }
    };

    // 사업장/평가기간이 바뀌면 PIVOT·기타노무비 재조회 (급여가져오기와 무관).
    useEffect(() => {
      loadPivot();
      loadEtcLabor();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cd_biz, dt_from, dt_to]);

    const onSaveEtc = async () => {
      if (!cd_biz || !ym_end) {
        showDialog("alter", "사업장과 평가기간을 확인해 주세요.");
        return;
      }
      getGridCurrent("grid2").stopEditing?.();
      const rows = (getGridCurrent("grid2").getFilteredData?.() ?? [])
        .filter((r: any) => String(r.cd_etc ?? "").trim() !== "")
        .map((r: any) => ({ cd_etc: r.cd_etc, am_etc: r.am_etc ?? 0 }));
      const res: any = await callApi("SN_PMS_CLS_REG_002_EtcLabor_Save", {
        cd_firm: userInfo.cd_firm,
        cd_biz,
        ym_end,
        rows,
      });
      if (res?.status) {
        getGridCurrent("grid2").resetUpdate();
        loadEtcLabor();
      }
    };

    /**
     * 급여가져오기 — Douzone 급여 조회 후 공수 비율(Pivot)로 프로젝트별 급여를 배부하고,
     * 입력된 기타노무비 총액을 프로젝트 급여 비중으로 배부해 노무비배부 탭에 표시한다.
     */
    const onImportSalary = async () => {
      if (!cd_biz || !ym_end) {
        showDialog("alter", "사업장과 평가기간을 확인해 주세요.");
        return;
      }
      try {
        setLoading(true);

        // 1) 공수 비율 Pivot — 평가기간 변경 시 이미 재조회되므로 여기선 현재 값 그대로 사용.
        let cols: { cd_column: string; nm_column: string }[] = pivotColumns;
        let rows = pivotRows;

        // 2) Douzone 급여 조회 (사원별 지급총액)
        const res: any = await callApi("SN_PMS_CLS_REG_002_Salary", {
          cd_firm: userInfo.cd_firm,
          cd_biz,
          ym: ym_end,
        });
        let salary: any[] =
          res?.status && Array.isArray(res.data) ? res.data : [];

        // TEST: 실 급여가 없으면 테스트 급여로 배부(검증용). 실 연동 확정되면 제거.
        let usingTest = false;
        if (!salary.length) {
          usingTest = true;
          if (!rows.length) {
            const syn = buildSyntheticPivot(); // 공수도 없으면 설계 예시로
            cols = syn.cols;
            rows = syn.rows;
          }
          salary = rows.map((r: any, i: number) => ({
            cd_emp: r.cd_emp,
            nm_emp: r.nm_emp,
            am_pay: TEST_PAY[i % TEST_PAY.length],
          }));
        } else if (!rows.length) {
          showDialog("alter", "공수 비율 데이터가 없습니다. Pivot을 확인해 주세요.");
          return;
        }

        // 3) 기타노무비 총액 (그리드 현재값 기준 — 미저장 입력도 반영)
        const etcData = getGridCurrent("grid2").getFilteredData?.() ?? etcRows;
        let totalEtc = (etcData ?? []).reduce(
          (s: number, r: any) => s + num(r.am_etc),
          0
        );
        if (usingTest && totalEtc === 0) totalEtc = TEST_ETC; // 테스트 기타노무비

        // 4) 담당자×프로젝트 피벗 배부 + 소계/기타노무비/총계 (순수 계산부)
        // ponytail: 항목별 반올림 미적용 — 요구되면 최대잔여법 추가
        const dist = computeSalaryDistribution(salary, cols, rows, totalEtc);
        if (!dist.projects.length) {
          showDialog("alter", "프로젝트 공수 비율이 없습니다.");
          return;
        }
        setDistColumns(buildDistColumns(dist.projects));
        setDistRows(dist.rows);
        setProjectTotals(dist.projectTotals);
        setTab("3"); // 노무비배부 탭으로 이동
        if (usingTest) {
          showDialog(
            "alter",
            "실 급여 데이터가 없어 테스트 급여로 배부했습니다.(검증용)"
          );
        }
      } finally {
        setLoading(false);
      }
    };

    // 요약 — 노무비배부 결과 기준(급여총액/기타노무비/총액)
    const sumPay = projectTotals.reduce((s, r) => s + num(r.am_pay), 0);
    const sumEtc = projectTotals.reduce((s, r) => s + num(r.am_etc), 0);
    const won = (v: number) => Math.round(v).toLocaleString();

    return (
      <div className="flex flex-col h-full overflow-hidden">
        <CommonSpinner isLoading={loading} />
        <div className="flex-shrink-0">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
            onValueChanged={onValueChanged}
          />
        </div>

        {/* 요약 — 급여총액 / 기타노무비 / 총액 */}
        <div className="flex-shrink-0 flex items-center gap-8 px-3 py-2 border-y bg-gray-50">
          {[
            { label: "급여총액", value: sumPay },
            { label: "기타노무비", value: sumEtc },
            { label: "총액", value: sumPay + sumEtc },
          ].map((f) => (
            <div key={f.label} className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{f.label}</span>
              <span className="min-w-[120px] text-right text-sm font-semibold tabular-nums">
                {won(f.value)}
              </span>
            </div>
          ))}
        </div>

        <div className="relative flex-1 min-h-0 mt-3">
          {/* 급여가져오기 — 탭 우측 상단(설계 위치) */}
          <div className="absolute right-0 top-0 z-10">
            <SnButton text="급여가져오기" onClick={onImportSalary} />
          </div>

          <SnTab
            titles={["Pivot", "기타노무비입력", "노무비배부"]}
            width={"100%"}
            selectedTab={tab}
            onTabChanged={(index: any) => setTab(index)}
            body={[
              // 0 Pivot — 비율 Pivot
              <SnGrid
                key="tab-mh"
                ref={gridRefs.current["grid1"]}
                columnConfig={pivotColumns}
                rowData={pivotRows}
                etcProps={{ rowSelection: "single", isEditable: false, title: "" }}
              />,
              // 1 기타노무비입력 — COS009 항목 자동표시, 금액만 입력, 하단 저장
              <div key="tab-etc" className="flex flex-col h-full min-h-0">
                <div className="flex-1 min-h-0">
                  <SnGrid
                    ref={gridRefs.current["grid2"]}
                    columnConfig={gridMeta.grid2 ?? []}
                    rowData={etcRows}
                    etcProps={{ isEditable: true, rowSelection: "single", title: "" }}
                  />
                </div>
                <div className="flex-shrink-0 flex justify-center py-2">
                  <SnButton text="저장" onClick={onSaveEtc} />
                </div>
              </div>,
              // 2 노무비배부 — 담당자×프로젝트 피벗 + 소계/기타노무비/총계
              <SnGrid
                key="tab-alloc"
                ref={gridRefs.current["grid3"]}
                columnConfig={distColumns}
                rowData={distRows}
                etcProps={{ rowSelection: "single", isEditable: false, title: "" }}
              />,
            ]}
          />
        </div>
      </div>
    );
  }
);

export default SN_PMS_CLS_REG_002_POP_01;
